import { Switch } from 'antd'
import { Link } from 'react-router-dom'

import LeftArrow from '@/assets/icons/left-arrow.svg?react'
import { Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Divider } from '@/components/base/Divider'
import { EmptyView } from '@/components/EmptyView'
import { PermissionFormProvider } from '@/pages/teams/permissionQuota/PermissionFormContext'
import type { Scope } from '@/pages/teams/permissionQuota/types'
import { useResourceACL } from '@/pages/teams/permissionQuota/useResourceACL'
import { useTeamStore } from '@/stores/team'

import { InstancePermission } from '../../permissionQuota/components/InstancePermission'
import { InstanceQuota } from '../../permissionQuota/components/InstanceQuota'
import { Others } from '../../permissionQuota/components/Others'
import { RegionPermission } from '../../permissionQuota/components/RegionPermission'
import { getPermissionOverMax } from '../../permissionQuota/validation'

function EmptyViewHint({ children }: { children: React.ReactNode }) {
  return (
    <EmptyView
      title={
        <Trans i18nKey="team.member.permission.usingTeamDefault">
          This user is now using team default permission
        </Trans>
      }
      content={children}
    />
  )
}

function InheritToggle({
  inherit,
  onChange,
}: {
  inherit: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <Switch checkedChildren="ON" unCheckedChildren="OFF" checked={inherit} onChange={onChange} />
  )
}

export function ResourcePermissionPage({
  scope,
  backTo,
  resId,
  titleTrail,
}: {
  scope: Scope
  backTo: string
  resId: string
  titleTrail: React.ReactNode
}) {
  const teamResID = useTeamStore((s) => s.teamTokenData?.teamResID) ?? ''

  const {
    loading,
    form,
    setForm,
    teamDefaults,
    save,
    resetFormToTeamDefaults,
    enterMemberMode,
    quotaLimits,
  } = useResourceACL(scope, teamResID, resId)

  const inherit = scope === 'member' ? !form.enableACL : false

  const onToggleInherit = (nextInherit: boolean) => {
    if (scope !== 'member') return
    if (nextInherit) {
      setForm({ ...form, enableACL: false })
    } else {
      enterMemberMode()
    }
  }

  const { overAny } = getPermissionOverMax(form, quotaLimits)

  const disableSave = loading || (scope === 'member' ? (form.enableACL ? overAny : false) : overAny)

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="h6 flex items-center gap-2">
          {scope === 'member' && (
            <>
              <Link
                to={backTo}
                className="w-11 h-11 icon-btn inline-flex items-center justify-center"
                aria-label="Back"
              >
                <LeftArrow />
              </Link>
            </>
          )}
          <span className="text-secondary">{titleTrail}</span>
          <span className="text-primary">
            <Trans i18nKey="team.member.permission.editPermission">Edit Permission</Trans>
          </span>
        </div>

        <div className="flex items-center gap-2">
          {scope === 'member' && form.enableACL && (
            <Button
              type="default"
              className="t-sb px-4 cursor-pointer"
              onClick={resetFormToTeamDefaults}
              disabled={loading}
            >
              <Trans i18nKey="team.member.permission.resetToDefault">Reset All to default</Trans>
            </Button>
          )}

          <Button
            className="t-sb w-20 cursor-pointer"
            type="secondary"
            onClick={save}
            disabled={disableSave}
          >
            <Trans i18nKey="team.member.permission.save">Save</Trans>
          </Button>
        </div>
      </div>

      <Divider />
      {scope === 'member' && (
        <div className="border border-neutral-200 rounded-2xl px-4 py-3 mb-6 flex items-center">
          <div className="flex-1">
            <div className="t-sb">
              <Trans i18nKey="team.member.permission.useTeamDefaultPermission">
                Use Team Default Permission
              </Trans>
            </div>
            <div className="t-sm text-secondary">
              <Trans i18nKey="team.member.permission.useTeamDefaultPermissionContent">
                Inherits permissions from team defaults. Turn off this option to customize user
                permissions.
              </Trans>
            </div>
          </div>
          <InheritToggle inherit={inherit} onChange={onToggleInherit} />
        </div>
      )}

      <PermissionFormProvider
        value={{
          scope,
          form,
          setForm: (next) => {
            if (typeof next === 'function') {
              setForm(next(form))
            } else {
              setForm(next)
            }
          },
          teamDefaults,
          loading,
          quotaLimits,
        }}
      >
        {scope === 'member' && !form.enableACL ? (
          <EmptyViewHint>
            <></>
          </EmptyViewHint>
        ) : (
          <div>
            <RegionPermission />
            <InstancePermission />
            <InstanceQuota />
            {/* todo: when APIs ready */}
            <Others />
          </div>
        )}
      </PermissionFormProvider>
    </div>
  )
}
